/**
 * Admin · Corporate Tenants list + create.
 *
 * Lives under `/admin/corporate` — reuses the existing console-admin
 * cookie session (same guard as escape_admin / clubs_admin). Console
 * admins land here to create a new corporate tenant (Acme etc.), edit
 * an existing one, view its members, or offboard people.
 */
import React, { useEffect, useMemo, useState } from "react";
import { corporateApi } from "@/lib/corporate-api";

const T = {
  bg: "#F4EFE6", card: "#FCFAF4", ink: "#211C15", ink2: "#5C5447",
  ink3: "#8A8071", line: "#E5DCCB", line2: "#D8CDB7", brass: "#8B6914",
  danger: "#B84B3C",
};

const DEFAULT_BENEFITS = [
  "table", "stay", "fit", "airport", "care", "escape", "enjoy", "maison",
];

export default function AdminCorporateTenants() {
  const [tenants, setTenants]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err,     setErr]         = useState(null);
  const [showNew, setShowNew]     = useState(false);
  const [selected, setSelected]   = useState(null);   // full tenant doc for edit-drawer
  const [members, setMembers]     = useState(null);   // members of selected

  async function refresh() {
    setLoading(true); setErr(null);
    try {
      const res = await corporateApi.admin.listTenants();
      setTenants(res.tenants || []);
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  async function openTenant(id) {
    try {
      const [t, m] = await Promise.all([
        corporateApi.admin.getTenant(id),
        corporateApi.admin.listMembers(id),
      ]);
      setSelected(t);
      setMembers(m.members || []);
    } catch (e) {
      alert(e.message || "Failed to open tenant");
    }
  }

  return (
    <div data-testid="corp-admin-page"
         style={{ minHeight: "100vh", background: T.bg, color: T.ink,
                  padding: "32px 32px 64px",
                  fontFamily: "'Hanken Grotesk', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <p style={{ letterSpacing: "0.14em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 8px",
                    fontWeight: 700 }}>
          Admin · Corporate portal
        </p>
        <div style={{ display: "flex", alignItems: "flex-end",
                      justifyContent: "space-between", gap: 24,
                      marginBottom: 28 }}>
          <h1 style={{ fontFamily: "'Fraunces', Georgia, serif",
                       fontSize: 32, fontWeight: 500, margin: 0 }}>
            Sponsor tenants
          </h1>
          <button
            data-testid="corp-admin-new-tenant-btn"
            type="button"
            onClick={() => setShowNew(true)}
            style={{
              background: T.ink, color: T.bg, border: 0, borderRadius: 999,
              padding: "10px 20px", fontSize: 12, fontWeight: 700,
              letterSpacing: "0.10em", textTransform: "uppercase",
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            + New tenant
          </button>
        </div>

        {err && (
          <div data-testid="corp-admin-err"
               style={{ background: "#FDECE9", border: `1px solid ${T.danger}55`,
                        borderRadius: 8, padding: "12px 16px", marginBottom: 20,
                        color: T.danger, fontSize: 13 }}>
            {err}
          </div>
        )}

        {loading ? (
          <div style={{ color: T.ink3 }}>Loading…</div>
        ) : tenants.length === 0 ? (
          <div data-testid="corp-admin-empty"
               style={{ background: T.card, border: `1px dashed ${T.line2}`,
                        borderRadius: 12, padding: "48px 32px", textAlign: "center",
                        color: T.ink3 }}>
            No corporate tenants yet. Click <strong>+ New tenant</strong>{" "}
            to enrol a sponsoring company.
          </div>
        ) : (
          <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: 14 }}>
            {tenants.map((t) => (
              <button
                key={t.id}
                data-testid={`corp-admin-tenant-${t.slug}`}
                type="button"
                onClick={() => openTenant(t.id)}
                style={{
                  background: T.card, border: `1px solid ${T.line}`,
                  borderRadius: 14, padding: "20px 22px", textAlign: "left",
                  cursor: "pointer", fontFamily: "inherit", color: T.ink,
                }}
              >
                <div style={{ letterSpacing: "0.14em", fontSize: 10.5,
                              color: T.brass, textTransform: "uppercase",
                              marginBottom: 6, fontWeight: 700 }}>
                  /{t.slug}
                </div>
                <div style={{ fontFamily: "'Fraunces', Georgia, serif",
                              fontSize: 20, fontWeight: 500,
                              margin: "0 0 8px" }}>
                  {t.display_name}
                </div>
                <div style={{ color: T.ink3, fontSize: 12, marginBottom: 8 }}>
                  {(t.email_domains || []).map((d) => `@${d}`).join(" · ")}
                </div>
                <div style={{ display: "flex", gap: 12,
                              fontSize: 11.5, color: T.ink2 }}>
                  <span>👥 {t.active_member_count || 0} active</span>
                  <span style={{ color: t.active ? "#3E7A4E" : T.danger }}>
                    {t.active ? "● Active" : "○ Inactive"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {showNew && (
        <NewTenantDrawer
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); refresh(); }}
        />
      )}
      {selected && (
        <EditTenantDrawer
          tenant={selected}
          members={members || []}
          onClose={() => { setSelected(null); setMembers(null); }}
          onChanged={() => { refresh(); }}
          onOffboard={async (mid) => {
            if (!window.confirm("Offboard this member? Their sessions will be revoked.")) return;
            try {
              await corporateApi.admin.offboardMember(selected.id, mid);
              const m = await corporateApi.admin.listMembers(selected.id);
              setMembers(m.members || []);
            } catch (e) { alert(e.message); }
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// New Tenant Drawer
// ─────────────────────────────────────────────────────────
function NewTenantDrawer({ onClose, onCreated }) {
  const [form, setForm] = useState({
    slug: "", legal_name: "", display_name: "",
    email_domains: "",
    logo_url: "", accent_color: "#8B6914",
    concierge_whatsapp: "+918792622424",
    concierge_email:    "concierge@lesconcierge.com",
    benefits: DEFAULT_BENEFITS.slice(),
  });
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleBenefit = (b) => setForm((s) => ({
    ...s,
    benefits: s.benefits.includes(b)
      ? s.benefits.filter((x) => x !== b)
      : [...s.benefits, b],
  }));

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await corporateApi.admin.createTenant({
        slug:               form.slug.trim().toLowerCase(),
        legal_name:         form.legal_name.trim(),
        display_name:       form.display_name.trim(),
        email_domains:      form.email_domains.split(/[,\s]+/).map((d) => d.trim()).filter(Boolean),
        logo_url:           form.logo_url.trim() || undefined,
        accent_color:       form.accent_color || undefined,
        concierge_whatsapp: form.concierge_whatsapp.trim() || undefined,
        concierge_email:    form.concierge_email.trim() || undefined,
        benefits_enabled:   form.benefits,
      });
      onCreated();
    } catch (e) {
      setErr(e.message || "Create failed");
    } finally { setBusy(false); }
  }

  return (
    <Drawer onClose={onClose} title="New corporate tenant"
            testid="corp-admin-new-tenant-drawer">
      <form onSubmit={onSubmit}>
        <Field label="Slug (URL path)" hint="Employees will land at lesconcierge.com/{slug}. Lowercase letters, digits, hyphens. Cannot be a reserved route.">
          <input data-testid="corp-admin-new-slug"
                 required value={form.slug}
                 onChange={(e) => setField("slug", e.target.value)}
                 placeholder="acme" style={inputStyle} />
        </Field>
        <Field label="Display name">
          <input data-testid="corp-admin-new-display-name"
                 required value={form.display_name}
                 onChange={(e) => setField("display_name", e.target.value)}
                 placeholder="Acme" style={inputStyle} />
        </Field>
        <Field label="Legal name">
          <input data-testid="corp-admin-new-legal-name"
                 required value={form.legal_name}
                 onChange={(e) => setField("legal_name", e.target.value)}
                 placeholder="Acme Corp Pvt Ltd" style={inputStyle} />
        </Field>
        <Field label="Accepted email domains" hint="Comma or space separated · @ prefix optional.">
          <input data-testid="corp-admin-new-domains"
                 required value={form.email_domains}
                 onChange={(e) => setField("email_domains", e.target.value)}
                 placeholder="acme.com, acme-india.com" style={inputStyle} />
        </Field>
        <Field label="Concierge WhatsApp">
          <input data-testid="corp-admin-new-wa"
                 value={form.concierge_whatsapp}
                 onChange={(e) => setField("concierge_whatsapp", e.target.value)}
                 placeholder="+918792622424" style={inputStyle} />
        </Field>
        <Field label="Concierge email">
          <input data-testid="corp-admin-new-email"
                 type="email" value={form.concierge_email}
                 onChange={(e) => setField("concierge_email", e.target.value)}
                 style={inputStyle} />
        </Field>
        <Field label="Benefits enabled" hint="Clubs stays as an upsell to MindEscapes and is intentionally not toggleable here.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {DEFAULT_BENEFITS.map((b) => (
              <label key={b} data-testid={`corp-admin-new-benefit-${b}`}
                     style={{
                       display: "flex", alignItems: "center", gap: 6,
                       background: form.benefits.includes(b) ? T.brass : T.card,
                       color: form.benefits.includes(b) ? T.bg : T.ink2,
                       border: `1px solid ${form.benefits.includes(b) ? T.brass : T.line2}`,
                       borderRadius: 999, padding: "6px 12px",
                       fontSize: 12, cursor: "pointer",
                     }}>
                <input type="checkbox" checked={form.benefits.includes(b)}
                       onChange={() => toggleBenefit(b)}
                       style={{ display: "none" }} />
                /{b}
              </label>
            ))}
          </div>
        </Field>

        {err && (
          <div data-testid="corp-admin-new-err"
               style={{ background: "#FDECE9", border: `1px solid ${T.danger}55`,
                        borderRadius: 8, padding: 12, marginTop: 16,
                        color: T.danger, fontSize: 13 }}>
            {err}
          </div>
        )}
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button type="button" onClick={onClose}
                  data-testid="corp-admin-new-cancel"
                  style={{ ...secondaryBtn, flex: 1 }}>
            Cancel
          </button>
          <button data-testid="corp-admin-new-submit"
                  type="submit" disabled={busy}
                  style={{ ...primaryBtn, flex: 2,
                           opacity: busy ? 0.6 : 1,
                           cursor: busy ? "not-allowed" : "pointer" }}>
            {busy ? "Creating…" : "Create tenant"}
          </button>
        </div>
      </form>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────
// Edit Tenant Drawer · shows members inline
// ─────────────────────────────────────────────────────────
function EditTenantDrawer({ tenant, members, onClose, onChanged, onOffboard }) {
  const [form, setForm] = useState({
    display_name:       tenant.display_name || "",
    email_domains:      (tenant.email_domains || []).join(", "),
    concierge_whatsapp: tenant.concierge_whatsapp || "",
    concierge_email:    tenant.concierge_email || "",
    active:             !!tenant.active,
    benefits:           tenant.benefits_enabled || [],
  });
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleBenefit = (b) => setForm((s) => ({
    ...s,
    benefits: s.benefits.includes(b) ? s.benefits.filter((x) => x !== b) : [...s.benefits, b],
  }));

  async function onSave() {
    setErr(null); setBusy(true);
    try {
      await corporateApi.admin.updateTenant(tenant.id, {
        display_name:       form.display_name.trim(),
        email_domains:      form.email_domains.split(/[,\s]+/).map((d) => d.trim()).filter(Boolean),
        concierge_whatsapp: form.concierge_whatsapp.trim() || undefined,
        concierge_email:    form.concierge_email.trim() || undefined,
        active:             form.active,
        benefits_enabled:   form.benefits,
      });
      onChanged();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const activeMembers = useMemo(
    () => (members || []).filter((m) => m.status !== "offboarded"),
    [members]
  );

  return (
    <Drawer onClose={onClose} title={`${tenant.display_name} · /${tenant.slug}`}
            testid={`corp-admin-edit-drawer-${tenant.slug}`}>
      <Field label="Display name">
        <input value={form.display_name}
               onChange={(e) => setField("display_name", e.target.value)}
               style={inputStyle} />
      </Field>
      <Field label="Accepted email domains">
        <input value={form.email_domains}
               onChange={(e) => setField("email_domains", e.target.value)}
               style={inputStyle} />
      </Field>
      <Field label="Concierge WhatsApp">
        <input value={form.concierge_whatsapp}
               onChange={(e) => setField("concierge_whatsapp", e.target.value)}
               style={inputStyle} />
      </Field>
      <Field label="Concierge email">
        <input type="email" value={form.concierge_email}
               onChange={(e) => setField("concierge_email", e.target.value)}
               style={inputStyle} />
      </Field>
      <Field label="Active">
        <label style={{ display: "flex", alignItems: "center", gap: 8,
                        color: T.ink2, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={form.active}
                 onChange={(e) => setField("active", e.target.checked)} />
          Sign-in is currently {form.active ? "enabled" : "disabled"}
        </label>
      </Field>
      <Field label="Benefits enabled">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {DEFAULT_BENEFITS.map((b) => (
            <label key={b}
                   style={{
                     background: form.benefits.includes(b) ? T.brass : T.card,
                     color: form.benefits.includes(b) ? T.bg : T.ink2,
                     border: `1px solid ${form.benefits.includes(b) ? T.brass : T.line2}`,
                     borderRadius: 999, padding: "6px 12px",
                     fontSize: 12, cursor: "pointer",
                   }}>
              <input type="checkbox" checked={form.benefits.includes(b)}
                     onChange={() => toggleBenefit(b)}
                     style={{ display: "none" }} />
              /{b}
            </label>
          ))}
        </div>
      </Field>

      {err && (
        <div style={{ background: "#FDECE9", border: `1px solid ${T.danger}55`,
                      borderRadius: 8, padding: 12, marginTop: 16,
                      color: T.danger, fontSize: 13 }}>{err}</div>
      )}
      <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
        <button type="button" onClick={onClose}
                style={{ ...secondaryBtn, flex: 1 }}>Cancel</button>
        <button type="button" onClick={onSave} disabled={busy}
                data-testid={`corp-admin-edit-save-${tenant.slug}`}
                style={{ ...primaryBtn, flex: 2,
                         opacity: busy ? 0.6 : 1,
                         cursor: busy ? "not-allowed" : "pointer" }}>
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Members table */}
      <div style={{ marginTop: 32, paddingTop: 20,
                    borderTop: `1px solid ${T.line}` }}>
        <p style={{ letterSpacing: "0.14em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 12px",
                    fontWeight: 700 }}>
          Members · {activeMembers.length} active
        </p>
        {(!members || members.length === 0) ? (
          <p style={{ color: T.ink3, fontSize: 13 }}>
            No members have signed in yet.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {members.map((m) => (
              <div key={m.id}
                   data-testid={`corp-admin-member-${m.id}`}
                   style={{
                     display: "grid",
                     gridTemplateColumns: "1fr auto auto",
                     gap: 12, alignItems: "center",
                     padding: "10px 14px",
                     background: m.status === "offboarded" ? "transparent" : T.card,
                     border: `1px solid ${T.line}`, borderRadius: 8,
                     opacity: m.status === "offboarded" ? 0.55 : 1,
                   }}>
                <div>
                  <div style={{ fontSize: 13, color: T.ink, fontWeight: 500 }}>
                    {m.first_name || "—"} {m.last_name || ""}
                  </div>
                  <div style={{ fontSize: 11.5, color: T.ink3,
                                fontFamily: "ui-monospace, monospace" }}>
                    {m.email}
                  </div>
                </div>
                <span style={{ fontSize: 10.5, letterSpacing: "0.12em",
                               textTransform: "uppercase", color: T.ink3 }}>
                  {m.status}
                </span>
                {m.status !== "offboarded" && (
                  <button
                    data-testid={`corp-admin-offboard-${m.id}`}
                    type="button"
                    onClick={() => onOffboard(m.id)}
                    style={{ background: "transparent", color: T.danger,
                             border: `1px solid ${T.danger}44`,
                             borderRadius: 999, padding: "4px 12px",
                             fontSize: 11, fontWeight: 600, cursor: "pointer",
                             fontFamily: "inherit" }}>
                    Offboard
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────
// Small primitives
// ─────────────────────────────────────────────────────────
function Drawer({ onClose, title, testid, children }) {
  return (
    <div data-testid={testid}
         style={{ position: "fixed", inset: 0, zIndex: 100,
                  background: "rgba(33,28,21,0.35)",
                  display: "flex", justifyContent: "flex-end" }}
         onClick={onClose}>
      <aside onClick={(e) => e.stopPropagation()}
             style={{ width: "min(520px, 100%)", height: "100%",
                      background: T.bg, overflowY: "auto",
                      padding: "28px 32px 40px",
                      boxShadow: "-24px 0 60px rgba(33,28,21,0.24)",
                      fontFamily: "'Hanken Grotesk', system-ui, sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif",
                       fontSize: 22, fontWeight: 500, margin: 0,
                       color: T.ink }}>{title}</h2>
          <button data-testid={`${testid}-close`}
                  type="button" onClick={onClose}
                  style={{ background: "transparent", border: 0,
                           fontSize: 20, cursor: "pointer",
                           color: T.ink3 }}>×</button>
        </div>
        {children}
      </aside>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 11,
                      letterSpacing: "0.14em", textTransform: "uppercase",
                      color: T.ink3, marginBottom: 6, fontWeight: 600 }}>
        {label}
      </label>
      {children}
      {hint && (
        <p style={{ margin: "6px 0 0", fontSize: 11.5, color: T.ink3,
                    lineHeight: 1.4 }}>{hint}</p>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: T.card, color: T.ink,
  border: `1px solid ${T.line2}`, borderRadius: 8,
  padding: "10px 12px", fontSize: 14, fontFamily: "inherit",
  outline: "none",
};

const primaryBtn = {
  background: T.ink, color: T.bg, border: 0, borderRadius: 999,
  padding: "12px 20px", fontSize: 12, fontWeight: 700,
  letterSpacing: "0.10em", textTransform: "uppercase",
  fontFamily: "inherit", cursor: "pointer",
};

const secondaryBtn = {
  background: "transparent", color: T.ink2,
  border: `1px solid ${T.line2}`, borderRadius: 999,
  padding: "12px 20px", fontSize: 12, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase",
  fontFamily: "inherit", cursor: "pointer",
};
